// src/components/CustomFields/ConversationOwner.tsx
// Single-select owner picker.  Fetches eligible people via GetListPeopleBySecurity;
// persists via POST conversation/UpdateOwnedConversation.

import { useState, useEffect, useRef } from 'react';
import { apiService } from '@services/api';
import CorgeeStorageHelper from '@/components/Globals/CorgeeStorageHelper';
import type { ConversationItem } from './types';

interface PersonItem {
  PersonID: number;
  Name: string;
}

interface ConversationOwnerProps {
  conversation: ConversationItem;
  isNew: boolean;
  disableAutoSave: boolean;
  onChangedOwner?: (conv: ConversationItem) => void;
  config?: any;
  configData?: any;
}

export default function ConversationOwner({
  conversation,
  isNew,
  disableAutoSave,
  onChangedOwner,
  config,
  configData: _configData,
}: ConversationOwnerProps)
{
  const [editorEnabled, setEditorEnabled] = useState(false);
  const [selectedId, setSelectedId] = useState<number | string>('');
  const [selectedName, setSelectedName] = useState('Not Set');
  const [originalSelectedId, setOriginalSelectedId] = useState<number | string>('');
  const [dataPersons, setDataPersons] = useState<PersonItem[]>([]);
  const [canEdit, setCanEdit] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const stateRef = useRef({ selectedId, selectedName, originalSelectedId });
  stateRef.current = { selectedId, selectedName, originalSelectedId };

  // --- Init from conversation ---
  useEffect(() =>
  {
    if (conversation?.OwnedById)
    {
      setSelectedId(conversation.OwnedById);
      setSelectedName(conversation.OwnedBy || 'Not Set');
      setOriginalSelectedId(conversation.OwnedById);
    }
  }, [conversation?.OwnedById, conversation?.OwnedBy]);

  // --- canEdit ---
  useEffect(() =>
  {
    const personId = CorgeeStorageHelper.personId();
    let canEditFixed = false;
    if (conversation)
    {
      canEditFixed = CorgeeStorageHelper.canEditOwner(conversation, personId);
    }
    if (isNew) canEditFixed = true;

    const personLayers = CorgeeStorageHelper.getAllLayers();
    if (personLayers.length === 1 && !isNew) canEditFixed = false;

    setCanEdit(canEditFixed);
  }, [conversation, isNew]);

  // --- Close on outside click ---
  useEffect(() =>
  {
    if (!editorEnabled) return;
    const timeoutId = setTimeout(() =>
    {
      const handler = (e: MouseEvent) =>
      {
        if (containerRef.current && !containerRef.current.contains(e.target as Node))
        {
          performSave();
          setEditorEnabled(false);
        }
      };
      document.addEventListener('mousedown', handler);
      (containerRef as any).__handler = handler;
    }, 100);
    return () =>
    {
      clearTimeout(timeoutId);
      const h = (containerRef as any).__handler;
      if (h)
      {
        document.removeEventListener('mousedown', h);
        delete (containerRef as any).__handler;
      }
    };
  }, [editorEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEnableEditor = async () =>
  {
    const projectId = conversation.ProjectID || conversation.ProjectId;
    const layerIds = (conversation.ConversationLayers || []).map(l => l.LayerId).join(',');

    const urlParams = [
      `configValueId=${conversation.selectedConfigValueId || conversation.ConfigValueId || ''}`,
      `projectId=${projectId || ''}`,
      `layerIdsSelected=${layerIds}`,
      `groupIdsSelected=`,
      `conversationId=${conversation.ConversationId || 0}`,
    ].join('&');

    try
    {
      const persons = await apiService.get<PersonItem[]>(
        `Person/GetListPeopleBySecurity?${urlParams}`,
      );
      setDataPersons(persons);
      // If only one option there is nothing to change
      if (persons.length <= 1) return;
      setEditorEnabled(true);
    }
    catch (err)
    {
      console.error('❌ Error loading people:', err);
    }
  };

  const handleSelectPerson = (person: PersonItem | null) =>
  {
    if (person)
    {
      setSelectedId(person.PersonID);
      setSelectedName(person.Name);
    }
    else
    {
      setSelectedId(0);
      setSelectedName('Not Set');
    }
    // Immediately close and save
    setEditorEnabled(false);
    setTimeout(() => performSave(), 0);
  };

  const performSave = () =>
  {
    const { selectedId: sId, selectedName: sName, originalSelectedId: oId } = stateRef.current;

    if (String(sId) === String(oId)) return;

    const conv: any = { ...conversation, OwnedById: sId, OwnedBy: sName };

    if (isNew)
    {
      if (onChangedOwner) onChangedOwner(conv);
      setOriginalSelectedId(sId);
    }
    else if (disableAutoSave)
    {
      // multi-edition mode: notify parent without making an API call
      if (onChangedOwner) onChangedOwner(conv);
      setOriginalSelectedId(sId);
      return;
    }

    if (!disableAutoSave)
    {
      if ((!isNew && sId && String(sId) !== '0') || isNew)
      {
        setOriginalSelectedId(sId);
        if (onChangedOwner) onChangedOwner(conv);

        if (config?.SocketConnectionId) conv.SocketConnectionId = config.SocketConnectionId;

        apiService
          .post('conversation/UpdateOwnedConversation', conv)
          .catch(err => console.error('❌ Error saving owner:', err));
      }
    }
  };

  // --- Render ---
  if (!canEdit)
  {
    return <div className="text-sm text-gray-600">{selectedName}</div>;
  }

  if (editorEnabled)
  {
    return (
      <div ref={containerRef} className="relative">
        <div className="text-sm text-corgee-blue font-medium mb-1">{selectedName}</div>
        <div className="absolute z-50 mt-0.5 bg-white border border-gray-200 rounded-md shadow-lg min-w-[160px] max-h-48 overflow-y-auto">
          <div
            className="px-3 py-1.5 text-sm text-gray-500 italic cursor-pointer hover:bg-gray-50"
            onClick={() => handleSelectPerson(null)}
          >
            Not Set
          </div>
          {dataPersons.map(p => (
            <div
              key={p.PersonID}
              className={[
                'px-3 py-1.5 text-sm cursor-pointer hover:bg-gray-50',
                String(selectedId) === String(p.PersonID) ? 'bg-gray-100 font-medium' : '',
              ].join(' ')}
              onClick={() => handleSelectPerson(p)}
            >
              {p.Name}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="lbl-editable-input" onClick={handleEnableEditor}>
      {selectedName}
    </div>
  );
}
